import { useT, type Translate } from "../../../shared/i18n";
import { messages, type MessageKey } from "../../../shared/i18n/messages";
import { Card, Table } from "../../../shared/ui";
import { KeyValueList } from "../KeyValueList";
import { formatBytes, formatDateTime } from "./format";
import type { StorageItem, UserStorage } from "./usersApi";

/** Server label keys are translated when known, else the kind is shown. */
function itemLabel(t: Translate, item: StorageItem): string {
  return item.label_key in messages
    ? t(item.label_key as MessageKey)
    : item.kind;
}

/** Technical footprint only: counts, sizes, timestamps. Never any content. */
export function StorageSection({ storage }: { storage: UserStorage }) {
  const t = useT();
  return (
    <Card title={t("admin.users.storage.title")}>
      <div className="flex flex-col gap-3">
        <p className="text-footnote text-fg-muted">
          {t("admin.users.storage.notice")}
        </p>
        <KeyValueList
          items={[
            {
              label: t("admin.users.storage.totalAbilities"),
              value: String(storage.totals.abilities_count),
            },
            {
              label: t("admin.users.storage.totalRecords"),
              value: String(storage.totals.records_count),
            },
            {
              label: t("admin.users.storage.totalBytes"),
              value: formatBytes(storage.totals.bytes_total),
            },
          ]}
        />
        <Table
          caption={t("admin.users.storage.itemsCaption")}
          rows={storage.items}
          rowKey={(row) => row.kind}
          columns={[
            {
              id: "store",
              header: t("admin.users.storage.col.store"),
              cell: (row) => itemLabel(t, row),
            },
            {
              id: "count",
              header: t("admin.users.storage.col.count"),
              cell: (row) => String(row.count),
            },
            {
              id: "size",
              header: t("admin.users.storage.col.size"),
              cell: (row) => formatBytes(row.bytes),
            },
            {
              id: "updated",
              header: t("admin.users.storage.col.updated"),
              cell: (row) => formatDateTime(row.updated_at),
            },
          ]}
        />
        <Table
          caption={t("admin.users.storage.abilitiesCaption")}
          rows={storage.abilities}
          rowKey={(row) => row.ability_instance_id}
          empty={
            <p className="text-body text-fg-muted">
              {t("admin.users.storage.noAbilities")}
            </p>
          }
          columns={[
            {
              id: "ability",
              header: t("admin.users.storage.col.ability"),
              cell: (row) => (
                <span className="font-mono text-footnote">
                  {row.ability_instance_id}
                </span>
              ),
            },
            {
              id: "status",
              header: t("admin.users.storage.col.status"),
              cell: (row) => row.status,
            },
            {
              id: "records",
              header: t("admin.users.storage.col.count"),
              cell: (row) => String(row.records_count),
            },
            {
              id: "size",
              header: t("admin.users.storage.col.size"),
              cell: (row) => formatBytes(row.bytes),
            },
            {
              id: "last",
              header: t("admin.users.storage.col.lastRecord"),
              cell: (row) => formatDateTime(row.last_record_at),
            },
          ]}
        />
      </div>
    </Card>
  );
}
